/**
 * S타입 - 보기선택 입력 컴포넌트
 * 다중선택 (ㄱㄴㄷㄹ)
 */

import React, { useState } from 'react';
import { PixelButton } from '../common/PixelButton';

/**
 * SelectionInput Props
 */
interface SelectionInputProps {
  /** 답안 제출 콜백 (배열로 전달) */
  onSubmit: (answer: string[]) => void;
  /** 비활성화 여부 */
  disabled?: boolean;
}

/**
 * S타입 보기선택 입력 컴포넌트
 *
 * - 8개의 체크박스 버튼 (ㄱㄴㄷㄹㅁㅂㅅㅇ)
 * - 다중 선택 가능
 * - 선택된 버튼 토글
 * - 제출 버튼
 */
export const SelectionInput: React.FC<SelectionInputProps> = ({
  onSubmit,
  disabled = false,
}) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const options = ['ㄱ', 'ㄴ', 'ㄷ', 'ㄹ', 'ㅁ', 'ㅂ', 'ㅅ', 'ㅇ'];

  /**
   * 보기 토글 핸들러
   */
  const handleToggle = (option: string) => {
    if (disabled) return;

    setSelected((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(option)) {
        newSet.delete(option);
      } else {
        newSet.add(option);
      }
      return newSet;
    });
  };

  /**
   * 제출 핸들러
   */
  const handleSubmit = () => {
    if (selected.size === 0 || disabled) return;

    // 선택된 보기를 배열로 변환하여 제출
    const answer = Array.from(selected).sort();
    onSubmit(answer);
    // 제출 후 초기화
    setSelected(new Set());
  };

  return (
    <div className="space-y-4">
      {/* 선택 안내 */}
      <p className="text-center font-pixel text-xs text-gray-400">
        해당하는 보기를 모두 선택하세요 (복수 선택 가능)
      </p>

      {/* 보기 버튼 (4x2 그리드) */}
      <div className="grid grid-cols-4 gap-2">
        {options.map((option) => {
          const isSelected = selected.has(option);
          return (
            <PixelButton
              key={option}
              variant={isSelected ? 'primary' : 'secondary'}
              size="small"
              onClick={() => handleToggle(option)}
              disabled={disabled}
              className="text-xl py-3 relative"
            >
              {/* 체크 표시 */}
              {isSelected && (
                <span className="absolute top-0 right-1 text-xs">✓</span>
              )}
              {option}
            </PixelButton>
          );
        })}
      </div>

      {/* 선택된 보기 표시 */}
      {selected.size > 0 && (
        <p className="text-center font-pixel text-sm text-[#9bbc0f]">
          선택: {Array.from(selected).sort().join(', ')}
        </p>
      )}

      {/* 제출 버튼 */}
      <PixelButton
        variant="primary"
        size="medium"
        onClick={handleSubmit}
        disabled={selected.size === 0 || disabled}
        className="w-full"
      >
        {selected.size === 0 ? '보기를 선택해주세요' : '제출하기'}
      </PixelButton>
    </div>
  );
};
